import React, { createContext, useContext, useState, useEffect } from 'react';

interface Permissions {
  create: boolean;
  read: boolean;
  update: boolean;
  delete: boolean;
}

interface User {
  id: number;
  username: string;
  nome: string;
  cognome: string;
  strutture: string[];
  hasTutteLeStrutture: boolean;
  permessi: {
    [entita: string]: Permissions;
  };
}

interface AuthContextType {
  user: User | null;
  login: (userData: User) => void;
  logout: () => void;
  isAuthenticated: boolean;
  isLoading: boolean;
  hasPermission: (entita: string, azione: 'create' | 'read' | 'update' | 'delete') => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Carica utente dal sessionStorage all'avvio
  useEffect(() => {
    const storedUser = sessionStorage.getItem('user');
    if (storedUser) {
      try {
        setUser(JSON.parse(storedUser));
      } catch (e) {
        console.error('Error parsing stored user:', e);
        sessionStorage.removeItem('user');
      }
    }
    setIsLoading(false);
  }, []);

  const login = (userData: User) => {
    setUser(userData);
    sessionStorage.setItem('user', JSON.stringify(userData));
  };

  const logout = () => {
    setUser(null);
    sessionStorage.removeItem('user');
  };

  const hasPermission = (entita: string, azione: 'create' | 'read' | 'update' | 'delete'): boolean => {
    if (!user || !user.permessi) {
      // Default: solo lettura se non ci sono permessi definiti
      return azione === 'read';
    }

    const permissions = user.permessi[entita];
    if (!permissions) {
      // Default: solo lettura per entità non definite
      return azione === 'read';
    }

    return permissions[azione] === true;
  };

  const isAuthenticated = user !== null;

  return (
    <AuthContext.Provider value={{ user, login, logout, isAuthenticated, isLoading, hasPermission }}>
      {children}
    </AuthContext.Provider>
  );
};
